// ═══════════════════════════════════════════════════════════════
// PIXALLOY — PSD Import Module
// ═══════════════════════════════════════════════════════════════
// Parses Adobe Photoshop PSD files using ag-psd and converts the
// layer tree into PixAlloy's layer format. Supports:
//   - Raster layers with pixel data
//   - Layer groups (folders)
//   - Blend modes (mapped to Canvas2D compositeOperation)
//   - Opacity, Visibility, Layer masks, Layer positioning
//
// Usage:
//   import { importPSD } from './psd-import.js';
//   const result = await importPSD(file);
//   // result = { width, height, layers: [...PixAlloy layer objects] }
// ═══════════════════════════════════════════════════════════════

import { readPsd } from 'ag-psd';
import { mkL, mkGroupL } from './engine/index.js';
import { ensurePsdCanvasInit } from './psd-canvas-init.js';

// ── Blend mode mapping: PSD → Canvas2D compositeOperation ──
const PSD_BLEND_TO_CANVAS = {
  'normal':       'source-over',
  'dissolve':     'source-over',
  'darken':       'darken',
  'multiply':     'multiply',
  'color burn':   'color-burn',
  'linear burn':  'color-burn',
  'darker color': 'darken',
  'lighten':      'lighten',
  'screen':       'screen',
  'color dodge':  'color-dodge',
  'linear dodge': 'color-dodge',
  'lighter color':'lighten',
  'overlay':      'overlay',
  'soft light':   'soft-light',
  'hard light':   'hard-light',
  'vivid light':  'hard-light',
  'linear light': 'hard-light',
  'pin light':    'hard-light',
  'hard mix':     'hard-light',
  'difference':   'difference',
  'exclusion':    'exclusion',
  'subtract':     'difference',
  'divide':       'source-over',
  'hue':          'hue',
  'saturation':   'saturation',
  'color':        'color',
  'luminosity':   'luminosity',
  'pass through': 'source-over',
};

function mapBlendMode(psdBlend) {
  return PSD_BLEND_TO_CANVAS[psdBlend || 'normal'] || 'source-over';
}

/**
 * Parse a PSD file and return PixAlloy-compatible layers.
 *
 * @param {File|ArrayBuffer} input — PSD file or ArrayBuffer of its contents
 * @returns {Promise<{width: number, height: number, layers: Array, layerCount: number, warnings: string[]}>}
 */
export async function importPSD(input) {
  ensurePsdCanvasInit();
  const warnings = [];

  // Read input as ArrayBuffer
  let buffer;
  if (input instanceof ArrayBuffer) {
    buffer = input;
  } else if (input instanceof Uint8Array) {
    buffer = input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
  } else if (input instanceof File || input instanceof Blob) {
    buffer = await input.arrayBuffer();
  } else {
    throw new Error('importPSD: input must be a File, Blob, ArrayBuffer, or Uint8Array');
  }

  // Parse PSD
  const psd = readPsd(new Uint8Array(buffer), {
    skipCompositeImageData: false,
    skipLayerImageData: false,
    skipThumbnail: true,
  });

  const { width, height } = psd;
  if (!width || !height) throw new Error('importPSD: PSD has no dimensions');

  const layers = [];

  function processLayer(psdLayer, depth = 0) {
    const name = psdLayer.name || `Layer ${layers.length + 1}`;
    const isGroup = psdLayer.children && psdLayer.children.length > 0;

    if (isGroup) {
      const group = mkGroupL(name, width, height);
      group.vis = !psdLayer.hidden;
      // ag-psd opacity is 0-1 (not 0-255)
      group.op = psdLayer.opacity ?? 1;
      group.blend = mapBlendMode(psdLayer.blendMode);
      group.collapsed = !psdLayer.opened;
      for (const child of psdLayer.children) {
        processLayer(child, depth + 1);
      }
      layers.push(group);
      return;
    }

    // Raster layer
    const layer = mkL(name, width, height, false);
    layer.vis = !psdLayer.hidden;
    layer.op = psdLayer.opacity ?? 1;
    layer.blend = mapBlendMode(psdLayer.blendMode);

    const left = psdLayer.left || 0;
    const top = psdLayer.top || 0;

    // Text layer — preserve textData for non-destructive editing
    if (psdLayer.text) {
      const td = psdLayer.text;
      const style = td.style || (td.styleRuns && td.styleRuns[0]?.style) || {};
      const fontSize = style.fontSize || 32;
      const fontName = style.font?.name || 'sans-serif';
      // Extract fill color from style (ag-psd uses {r,g,b} 0-255 RGBA)
      let color = '#000000';
      if (style.fillColor && 'r' in style.fillColor) {
        const c = style.fillColor;
        color = `rgb(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)})`;
      }
      const weight = style.fauxBold ? 'bold' : 'normal';
      // Use text bounds for positioning
      const textX = td.left ?? left;
      const textY = td.top ?? top;

      layer.textData = {
        text: td.text || '',
        font: fontName,
        size: fontSize,
        weight,
        color,
        align: 'left',
        x: textX,
        y: textY,
      };
      // Store the raw ag-psd text data for lossless PSD re-export
      layer._psdTextData = td;
      layer.name = 'Text: ' + (td.text || '').slice(0, 12);
    }

    try {
      if (psdLayer.canvas) {
        layer.x.drawImage(psdLayer.canvas, left, top);
      } else if (psdLayer.imageData) {
        const id = psdLayer.imageData instanceof ImageData
          ? psdLayer.imageData
          : new ImageData(
              new Uint8ClampedArray(psdLayer.imageData.data),
              psdLayer.imageData.width,
              psdLayer.imageData.height
            );
        layer.x.putImageData(id, left, top);
      } else {
        warnings.push(`Layer "${name}": no pixel data found (empty layer)`);
      }
    } catch (err) {
      warnings.push(`Layer "${name}": failed to draw pixel data — ${err.message}`);
    }

    // Layer mask
    if (psdLayer.mask) {
      const mask = psdLayer.mask;
      if (mask.left != null && mask.top != null) {
        try {
          const maskLeft = mask.left || 0;
          const maskTop = mask.top || 0;
          layer.maskCtx.clearRect(0, 0, width, height);
          if (mask.defaultColor === 255) {
            layer.maskCtx.fillStyle = '#fff';
            layer.maskCtx.fillRect(0, 0, width, height);
          }
          if (mask.canvas) {
            layer.maskCtx.drawImage(mask.canvas, maskLeft, maskTop);
          } else if (mask.imageData) {
            const maskId = mask.imageData instanceof ImageData
              ? mask.imageData
              : new ImageData(
                  new Uint8ClampedArray(mask.imageData.data),
                  mask.imageData.width,
                  mask.imageData.height
                );
            layer.maskCtx.putImageData(maskId, maskLeft, maskTop);
          }
          layer.maskEnabled = !mask.disabled;
        } catch (err) {
          warnings.push(`Layer "${name}": failed to import mask — ${err.message}`);
        }
      }
    }

    layers.push(layer);
  }

  // Process top-level children (or create single layer from composite)
  if (psd.children && psd.children.length > 0) {
    for (const child of psd.children) {
      processLayer(child);
    }
  } else {
    // No layer tree — create single layer from composite image
    const layer = mkL('Background', width, height, true);
    try {
      if (psd.canvas) {
        layer.x.drawImage(psd.canvas, 0, 0);
      } else if (psd.imageData) {
        const id = psd.imageData instanceof ImageData
          ? psd.imageData
          : new ImageData(new Uint8ClampedArray(psd.imageData.data), psd.imageData.width, psd.imageData.height);
        layer.x.putImageData(id, 0, 0);
      }
    } catch (err) {
      warnings.push('Composite image: failed to draw — ' + err.message);
    }
    layers.push(layer);
  }

  if (layers.length === 0) {
    layers.push(mkL('Background', width, height, true));
    warnings.push('No layers found — created empty background');
  }

  return { width, height, layers, layerCount: layers.length, warnings };
}
